import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

const UserAttendance = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [records, setRecords] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        month: '',
        week: '',
        date: ''
    });

    // Fetch all users on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API_BASE}/attendance_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_all_users'
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setUsers(data.users || []);
            } else {
                console.error('Error fetching users:', data.message);
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        }
    };

    const fetchAttendance = async () => {
        if (!selectedUser) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/attendance_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_user_attendance',
                    user_id: selectedUser,
                    month: filters.month,
                    week: filters.week,
                    date: filters.date
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setRecords(data.records || []);
                setUserDetails(data.user_details || null);
                setStatistics(data.statistics || null);
            } else {
                console.error('Error fetching attendance:', data.message);
                setRecords([]);
                setUserDetails(null);
                setStatistics(null);
            }
        } catch (error) {
            console.error('Error fetching attendance:', error);
            setRecords([]);
            setUserDetails(null);
            setStatistics(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        if (!selectedUser) return;

        try {
            const response = await fetch(`${API_BASE}/attendance_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'export_csv',
                    user_id: selectedUser,
                    month: filters.month,
                    week: filters.week
                })
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `attendance_${userDetails?.username || 'user'}_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Export failed with status:', response.status);
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return 'Active';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting time:', error);
            return 'Invalid Time';
        }
    };

    const handleClear = () => {
        setSelectedUser('');
        setFilters({ month: '', week: '', date: '' });
        setRecords([]);
        setUserDetails(null);
        setStatistics(null);
    };

    // Auto-fetch attendance when user is selected
    useEffect(() => {
        if (selectedUser) {
            fetchAttendance();
        }
    }, [selectedUser]);

    return (
        <Layout>
            <div style={{ padding: '20px', background: '#0f0c29', minHeight: '100vh', color: 'white' }}>
                <h2 style={{ marginBottom: '20px', color: 'white' }}>User Attendance Records</h2>
                
                {/* Filters */}
                <div style={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    padding: '20px', 
                    borderRadius: '10px', 
                    marginBottom: '20px' 
                }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Select User:</label>
                            <select 
                                value={selectedUser} 
                                onChange={(e) => setSelectedUser(e.target.value)}
                                style={{ 
                                    width: '100%', 
                                    padding: '10px', 
                                    borderRadius: '5px', 
                                    background: '#1a1a1a', 
                                    color: 'white', 
                                    border: '1px solid #333',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Choose a user...</option>
                                {users.map(user => (
                                    <option key={user.user_id} value={user.user_id}>
                                        {user.username} ({user.role})
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Month:</label>
                            <input 
                                type="month" 
                                value={filters.month}
                                onChange={(e) => setFilters({...filters, month: e.target.value})}
                                style={{ 
                                    width: '100%', 
                                    padding: '10px', 
                                    borderRadius: '5px', 
                                    background: '#1a1a1a', 
                                    color: 'white', 
                                    border: '1px solid #333',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Week:</label>
                            <input 
                                type="week" 
                                value={filters.week}
                                onChange={(e) => setFilters({...filters, week: e.target.value})}
                                style={{ 
                                    width: '100%', 
                                    padding: '10px', 
                                    borderRadius: '5px', 
                                    background: '#1a1a1a', 
                                    color: 'white', 
                                    border: '1px solid #333',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Date:</label>
                            <input 
                                type="date" 
                                value={filters.date}
                                onChange={(e) => setFilters({...filters, date: e.target.value})}
                                style={{ 
                                    width: '100%', 
                                    padding: '10px', 
                                    borderRadius: '5px', 
                                    background: '#1a1a1a', 
                                    color: 'white', 
                                    border: '1px solid #333',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                        <button 
                            onClick={fetchAttendance}
                            disabled={!selectedUser || loading}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#6366F1', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '5px',
                                cursor: selectedUser && !loading ? 'pointer' : 'not-allowed',
                                opacity: selectedUser && !loading ? 1 : 0.6,
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {loading ? 'Loading...' : 'Fetch Records'}
                        </button>
                        
                        <button 
                            onClick={handleExportCSV}
                            disabled={!selectedUser || records.length === 0}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#10B981', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '5px',
                                cursor: selectedUser && records.length > 0 ? 'pointer' : 'not-allowed',
                                opacity: selectedUser && records.length > 0 ? 1 : 0.6,
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Export CSV
                        </button>
                        
                        <button 
                            onClick={handleClear}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#6B7280', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* User Details */}
                {userDetails && (
                    <div style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        padding: '20px', 
                        borderRadius: '10px', 
                        marginBottom: '20px' 
                    }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>User: {userDetails.username}</h3>
                        <p style={{ margin: '0', opacity: 0.9 }}>
                           Role: {userDetails.role || 'N/A'}
                        </p>
                    </div>
                )}

                {/* Statistics */}
                {statistics && (
                    <div style={{ 
                        background: 'rgba(255,255,255,0.1)', 
                        padding: '20px', 
                        borderRadius: '10px', 
                        marginBottom: '20px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <h4 style={{ color: '#6366F1', margin: '0', fontSize: '24px' }}>{statistics.total_sessions}</h4>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>Total Sessions</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h4 style={{ color: '#10B981', margin: '0', fontSize: '24px' }}>{statistics.total_hours}h</h4>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>Total Hours</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h4 style={{ color: '#F59E0B', margin: '0', fontSize: '24px' }}>{statistics.avg_session_minutes}m</h4>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>Avg Session</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h4 style={{ color: '#8B5CF6', margin: '0', fontSize: '24px' }}>{statistics.active_days}</h4>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>Active Days</p>
                        </div>
                    </div>
                )}

                {/* Records Table */}
                {records.length > 0 && (
                    <div style={{ 
                        background: 'rgba(255,255,255,0.05)', 
                        borderRadius: '10px', 
                        overflow: 'hidden',
                        marginBottom: '20px'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                <thead>
                                    <tr style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Date</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Day</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Check In</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Check Out</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Duration</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Location</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Device</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', fontSize: '14px' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((record, index) => (
                                        <tr key={index} style={{ borderBottom: '1px solid #333', background: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{record.check_in_date}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{record.day_name}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{formatTime(record.check_in_time)}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                {record.check_out_time ? formatTime(record.check_out_time) : 'Active'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px', fontFamily: 'monospace' }}>
                                                {formatDuration(record.duration_seconds)}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                {record.check_in_location || 'N/A'}
                                                {record.check_out_location && record.check_out_location !== 'N/A' && ` → ${record.check_out_location}`}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{record.check_in_device || 'N/A'}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    background: record.check_out_time ? '#10B981' : '#F59E0B',
                                                    color: 'white',
                                                    fontWeight: '500'
                                                }}>
                                                    {record.check_out_time ? 'Completed' : 'Active'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {!loading && selectedUser && records.length === 0 && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#9CA3AF',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '10px'
                    }}>
                        No attendance records found for the selected filters.
                    </div>
                )}

                {loading && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#9CA3AF'
                    }}>
                        Loading attendance records...
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default UserAttendance;